import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';

import StructuredResult from './StructuredResult';

// ==============================|| MESSAGE BUBBLE ||============================== //

// react-markdown disables raw HTML by default; additionally restrict links to
// http(s) so a crafted answer can't emit javascript:/data: URLs.
export const markdownComponents = {
  a: ({ href, children }) => {
    const safe = typeof href === 'string' && /^https?:\/\//i.test(href);
    if (!safe) return <span>{children}</span>;
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },
  // associate GFM table header cells with their column for screen readers
  th: ({ children }) => <th scope="col">{children}</th>
};

const UserBubble = ({ content }) => (
  <Stack
    direction="row"
    sx={{
      justifyContent: 'flex-end',
      mb: 1.5
    }}
  >
    <Box
      sx={{
        maxWidth: '85%',
        px: 1.5,
        py: 1,
        borderRadius: 2,
        borderTopRightRadius: 4,
        bgcolor: 'primary.200',
        color: 'text.primary',
        // default selection highlight is too close to the light-blue bubble;
        // use a dark contrasting highlight so selected text stays readable
        '&::selection, & ::selection': {
          bgcolor: 'primary.dark',
          color: 'primary.contrastText'
        }
      }}
    >
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {content}
      </Typography>
    </Box>
  </Stack>
);

UserBubble.propTypes = {
  content: PropTypes.string
};

// Renders the final answer as markdown. remark-gfm enables GFM tables, which
// we style with borders + a header background and let overflow horizontally so
// wide firmware tables stay readable inside the narrow bubble.
const MarkdownAnswer = ({ text }) => (
  <Box
    sx={{
      fontSize: '0.875rem',
      lineHeight: 1.5,
      '& p': { m: 0 },
      '& p + p': { mt: 1 },
      '& table': {
        display: 'block',
        width: '100%',
        overflowX: 'auto',
        borderCollapse: 'collapse',
        my: 1,
        fontSize: '0.8125rem'
      },
      '& th, & td': {
        border: '1px solid',
        borderColor: 'divider',
        px: 1,
        py: 0.5,
        textAlign: 'left',
        whiteSpace: 'nowrap'
      },
      '& th': { bgcolor: 'action.hover', fontWeight: 600 }
    }}
  >
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
      {text}
    </ReactMarkdown>
  </Box>
);

MarkdownAnswer.propTypes = {
  text: PropTypes.string
};

const AssistantBubble = ({ message }) => {
  const { text, structured, status, error } = message;
  const isStreaming = status === 'streaming';
  const isError = status === 'error';

  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: 'flex-start',
        mb: 1.5
      }}
    >
      <Box
        sx={{
          maxWidth: '90%',
          px: 1.5,
          py: 1,
          borderRadius: 2,
          borderTopLeftRadius: 4,
          bgcolor: 'background.default',
          border: '1px solid',
          borderColor: 'divider',
          width: '100%'
        }}
      >
        {/* spinner while working — only the final answer is shown, so we simply
            wait until the answer text arrives */}
        {isStreaming && !text && (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center'
            }}
          >
            <CircularProgress size={14} thickness={6} />
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary'
              }}
            >
              Thinking…
            </Typography>
          </Stack>
        )}

        {/* error terminal event */}
        {isError && (
          <Typography variant="body2" color="error">
            {error?.errorMessage || 'Something went wrong.'}
          </Typography>
        )}

        {/* final answer text (markdown) */}
        {text && <MarkdownAnswer text={text} />}

        {/* structured devices / firmware lists */}
        {status === 'done' && <StructuredResult structured={structured} />}
      </Box>
    </Stack>
  );
};

AssistantBubble.propTypes = {
  message: PropTypes.object
};

const MessageBubble = ({ message }) => {
  if (message.role === 'user') return <UserBubble content={message.content} />;
  return <AssistantBubble message={message} />;
};

MessageBubble.propTypes = {
  message: PropTypes.object
};

export default MessageBubble;
